const normalizeServerUrl = (value) => {
    const url = value?.trim()?.replace(/\/+$/, "");
    if (!url) return "";
    return url.endsWith("/api") ? url : `${url}/api`;
};

const serverUrl = normalizeServerUrl(process.env.REACT_APP_SERVER);

const readJsonSafe = async (res) => {
  const text = await res.text().catch(() => "");
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (err) {
    const preview = text.slice(0, 120);
    throw new Error(
      `Expected JSON from ${res.url || "server"} but received ${res.status} ${res.statusText}: ${preview}`,
    );
  }
};

const getStoredToken = (key) => {
  const token = localStorage.getItem(key);
  return token && token !== "undefined" && token !== "null" ? token : null;
};

const hasRefreshToken = () => {
  return !!getStoredToken("ATRefTok");
};

const buildAuthHeaders = () => {
  const token = getStoredToken("ATAccTok");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQueryString = (params) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            searchParams.set(key, value);
        }
    });

    return searchParams.toString();
};

//---------------------------------------------------------------USER ACTIONS

//SignUp
export const signUpAPI = async (email, userName, password) => {
    try {
        assertServerUrl();
        const res = await fetch(`${serverUrl}/auth/signup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userName: userName,
                email: email,
                password: password
            }),
        });
        const data = await readJsonSafe(res).catch((err) => ({ message: err.message }));
        return { status: res.status, ok: res.ok, data };
    } catch (err) {
        console.error("signUpAPI error:", err);
        return { status: null, ok: false, data: null, error: err };
    }
};
//Login
export const loginAPI = async (email, password) => {
    try {
        assertServerUrl();
        const res = await fetch(`${serverUrl}/auth/signin`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: email,
                password: password
            }),
        });
        const data = await readJsonSafe(res).catch((err) => ({ message: err.message }));
        if (res.ok && data?.accessToken && data?.refreshToken) {
            localStorage.setItem("ATAccTok", data.accessToken);
            localStorage.setItem("ATRefTok", data.refreshToken);
        }
        return { status: res.status, ok: res.ok, data };
    } catch (err) {
        console.error(err);
        return { status: null, ok: false, data: null, error: err };
    }
};
//Change Password
export const changePassword = async (newPassword) => {
    try {
        const res = await fetch(`${serverUrl}/password/change`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem('ATAccTok')}`
            },
            body: JSON.stringify({
                "newPassword": newPassword
            }),
        });
        if (res.status === 401 && res.statusText === 'Unauthorized' && localStorage.getItem('ATAccTok') !== 'undefined' && localStorage.getItem('ATAccTok') !== '') {
            await getToken()
            return await changePassword(newPassword)
        }
        const data = await res?.json()
        return { status: res.status, ok: res.ok, message: data.message };
    } catch (err) {
        console.error(err);
        return { status: null, ok: false, data: null, error: err };
    }
};
//Change User Data 
export const changeUserData = async (userName, email) => {
    try {
        let userData = {}
        if (userName) userData.userName = userName
        if (email) userData.email = email
        const res = await fetch(`${serverUrl}/change`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem('ATAccTok')}`
            },
            body: JSON.stringify(userData),
        });
        if (res.status === 401 && res.statusText === 'Unauthorized' && localStorage.getItem('ATAccTok') !== 'undefined' && localStorage.getItem('ATAccTok') !== '') {
            await getToken()
            return await changeUserData(userName, email)
        }
        const data = await res?.json()
        return { status: res.status, ok: res.ok, message: data.message };
    } catch (err) {
        console.error(err);
        return { status: null, ok: false, data: null, error: err };
    }
};
//Get user data
export const getUserData = async () => {
    if (!getStoredToken("ATAccTok") && !hasRefreshToken()) {
        return { user: null };
    }

    try {
        assertServerUrl();
        const res = await fetch(`${serverUrl}/user-data`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    ...buildAuthHeaders(),
                },
            });
        if (res.status === 401 && hasRefreshToken()) {
            await getToken()
            return await getUserData()
        }
        if (res.status === 401) {
            return { user: null };
        }
        const data = await readJsonSafe(res);
        return data || { user: null };
    } catch (err) {
        console.error("getUserData error:", err);
        return { user: null, error: err };
    }
};

//-----------------------------------------------------------DATA

//Get All hotels or by cityId or stateId
export const getAllHotels = async (cityId, stateId) => {
    try {
        const res = await fetch(`${serverUrl}/hotels?${(cityId ? "cityId=" + cityId : '') + (cityId && stateId ? "&" : '') + (stateId ? "stateId=" + stateId : '')}`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });
        const data = await res.json();
        return data
    } catch (err) {
        console.error(err);
    }
};
//Get single hotel by hotelId
export const getHotel = async (hotelId) => {
    try {
        const res = await fetch(`${serverUrl}/hotels/${hotelId}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        return data
    } catch (err) {
        console.error(err);
    }
};

export const getHotelsByIds = async (ids) => {
    const hotelIds = [...new Set((ids || []).map(Number).filter(Number.isInteger))];
    if (hotelIds.length === 0) return [];
    try {
        const queryString = buildQueryString({ ids: hotelIds.join(',') });
        const res = await fetch(`${serverUrl}/hotels?${queryString}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        return await res.json();
    } catch (err) {
        console.error(err);
        return [];
    }
};

// Search the locally synchronized GIATA hotel database.
export const searchHotels = async (query, options = {}) => {
    try {
        const queryString = buildQueryString({
            query,
            state: options.state,
            city: options.city,
            pageSize: options.pageSize,
            page: options.page,
            reviewedOnly: options.reviewedOnly,
            sort: options.sort,
        });
        const res = await fetch(`${serverUrl}/hotels/search?${queryString}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
            return { hotels: [], error: data?.error || "Unable to search hotels." };
        }
        return {
            hotels: data?.hotels || [],
            page: data?.page || 1,
            hasMore: Boolean(data?.hasMore),
            error: null,
        };
    } catch (err) {
        console.error(err);
        return { hotels: [], error: "Unable to search hotels." };
    }
};

// Hotels inside the current map viewport. The list view is paginated, but the
// map refetches on every pan/zoom so it can show everything on screen.
export const getHotelsInBounds = async (bounds, options = {}) => {
    try {
        const queryString = buildQueryString({
            north: bounds?.north,
            south: bounds?.south,
            east: bounds?.east,
            west: bounds?.west,
            reviewedOnly: options.reviewedOnly,
            limit: options.limit,
        });
        const res = await fetch(`${serverUrl}/hotels/map?${queryString}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
            return { hotels: [], truncated: false, error: data?.error || "Unable to load hotels for this area." };
        }
        return { hotels: data?.hotels || [], truncated: Boolean(data?.truncated), error: null };
    } catch (err) {
        console.error("getHotelsInBounds error:", err);
        return { hotels: [], truncated: false, error: "Unable to load hotels for this area." };
    }
};

export const getHotelSuggestions = async (query, limit = 8) => {
    const normalizedQuery = String(query || '').trim();
    if (normalizedQuery.length < 2) return [];
    try {
        const queryString = buildQueryString({ query: normalizedQuery, limit });
        const res = await fetch(`${serverUrl}/hotels/suggestions?${queryString}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const data = await res.json().catch(() => null);
        return res.ok ? data?.suggestions || [] : [];
    } catch (err) {
        console.error("getHotelSuggestions error:", err);
        return [];
    }
};

// Fetch live Google Places hotel details by place ID. Store only placeId with Autie reviews.
export const getGoogleHotel = async (placeId) => {
    try {
        if (!placeId || /[,&=]/.test(String(placeId))) {
            return { error: "Invalid Google place ID" };
        }

        const res = await fetch(`${serverUrl}/hotels/google/${encodeURIComponent(placeId)}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) return { error: data?.error || "Hotel not found" };
        return data?.hotel;
    } catch (err) {
        console.error(err);
        return { error: "Hotel not found" };
    }
};
//Get states with their hotels and cities (for homepage search)
export const getStatesWithHotels = async () => {
    try {
        assertServerUrl();
        const res = await fetch(`${serverUrl}/hotels/states-with-hotels`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const data = await readJsonSafe(res);
        if (!res.ok) {
            return { states: [], cities: [], hotels: [], error: data?.message || data?.error || "Unable to fetch hotels." };
        }
        return data || { states: [], cities: [], hotels: [] };
    } catch (err) {
        console.error("getStatesWithHotels error:", err);
        return { states: [], cities: [], hotels: [], error: err };
    }
};

//----------------------------------------------------------REVIEWS

//Get reviews by hotelId or userId or both
export const getReviewsData = async (hotelId, userReview, googlePlaceId) => {
    try {
        let headerObject = {
            "Content-Type": "application/json",
        }
        if (userReview) headerObject.Authorization = `Bearer ${localStorage.getItem('ATAccTok')}`
        const queryString = buildQueryString({ hotelId, googlePlaceId });
        const res = await fetch(`${serverUrl}/reviews${queryString ? `?${queryString}` : ""}`, {
            method: "GET",
            headers: headerObject,
        });
        if (res.status === 401 && res.statusText === 'Unauthorized' && localStorage.getItem('ATAccTok') !== 'undefined' && localStorage.getItem('ATAccTok') !== '') {
            await getToken()
            return await getReviewsData(hotelId, userReview, googlePlaceId)
        }
        const data = await res?.json();
        return data
    } catch (err) {
        console.error(err);
    }
};
//Send review
export const sendReview = async (hotelID, review, googlePlaceId) => {
    try {
        const reviewBody = {
            "comment": review?.text,
            "ratesOptions": {
                "1": +review?.scent,
                "2": +review?.texture,
                "3": +review?.sound,
                "4": +review?.visual,
                "5": +review?.staff
            }
        };

        if (googlePlaceId) reviewBody.googlePlaceId = googlePlaceId;
        else reviewBody.hotelId = hotelID;

        const res = await fetch(`${serverUrl}/reviews`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem('ATAccTok')}`
            },
            body: JSON.stringify(reviewBody),
        });
        if (res.status === 401 && res.statusText === 'Unauthorized' && localStorage.getItem('ATAccTok') !== 'undefined' && localStorage.getItem('ATAccTok') !== '') {
            await getToken()
            return await sendReview(hotelID, review, googlePlaceId)
        }
        return { status: res.status, ok: res.ok, };
    } catch (err) {
        console.error(err);
        return { status: null, ok: false, data: null, error: err };
    }
};
//Edit Review
export const editReview = async (hotelID, review, googlePlaceId) => {
    try {
        const queryString = buildQueryString({ hotelId: hotelID, googlePlaceId });
        const res = await fetch(`${serverUrl}/reviews?${queryString}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem('ATAccTok')}`
            },
            body: JSON.stringify({
                "new_comment": review?.text,
                "rateOptions": {
                    "1": +review?.scent,
                    "2": +review?.texture,
                    "3": +review?.sound,
                    "4": +review?.visual,
                    "5": +review?.staff
                }
            }),
        });
        if (res.status === 401 && res.statusText === 'Unauthorized' && localStorage.getItem('ATAccTok') !== 'undefined' && localStorage.getItem('ATAccTok') !== '') {
            await getToken()
            return await editReview(hotelID, review, googlePlaceId)
        }
        return { status: res.status, ok: res.ok, };
    } catch (err) {
        console.error(err);
        return { status: null, ok: false, data: null, error: err };
    }
};

//-------------------------------------------------RESET PASSWORD

//Send Reset Email
export const resetPasswordEmail = async (email) => {
    try {
        assertServerUrl();
        const res = await fetch(`${serverUrl}/request-password-reset`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: email,
            }),
        });
        const data = await readJsonSafe(res).catch((err) => ({ message: err.message }));
        return { status: res.status, ok: res.ok, data };
    } catch (err) {
        console.error("signUpAPI error:", err);
        return { status: null, ok: false, data: null, error: err };
    }
};
//Reset Password with Email
export const resetPasswordPassword = async (token, newPassword) => {
    try {
        assertServerUrl();
        const res = await fetch(`${serverUrl}/request-password-reset`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                token: token,
                newPassword: newPassword
            }),
        });
        const data = await readJsonSafe(res).catch((err) => ({ message: err.message }));
        return { status: res.status, ok: res.ok, message: data?.message };
    } catch (err) {
        console.error(err);
        return { status: null, ok: false, data: null, error: err };
    }
};

//------------------------------------------------------SUPPORT

export const sendSupportMsg = async (email, subject, message) => {
    try {
        const res = await fetch(`${serverUrl}/support`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: email,
                subject: subject,
                message: message
            }),
        });
        const data = await res.json();
        return { status: res.status, ok: res.ok, data };
    } catch (err) {
        console.error("signUpAPI error:", err);
        return { status: null, ok: false, data: null, error: err };
    }
};

//---------------------------------------------------------------PRIVACY FUNCTIONS

const assertServerUrl = () => {
  if (!serverUrl) {
    throw new Error(
      "REACT_APP_SERVER is not set. Restart the frontend after editing .env",
    );
  }
};

const extractFilename = (res) => {
  const disposition = res.headers.get('content-disposition');
  if (disposition && disposition.includes('attachment')) {
    const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].replace(/['"]/g, '');
    }
  }
  return 'autie-data-export.json';
};

/**
 * Request a copy of all personal data (Subject Access Request)
 */
export const requestDataAccess = async () => {
  try {
    assertServerUrl();
    const res = await fetch(`${serverUrl}/privacy/data-access`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("ATAccTok")}`,
      },
    });

    if (res.status === 401 && hasRefreshToken()) {
      await getToken();
      return await requestDataAccess();
    }
    const data = await readJsonSafe(res).catch(() => null);
    return { status: res.status, ok: res.ok, data };
  } catch (err) {
    console.error("requestDataAccess error:", err);
    return { status: null, ok: false, data: null, error: err };
  }
};

/**
 * Request data export (structured, machine-readable format)
 */
export const requestDataExport = async () => {
  try {
    assertServerUrl();
    const res = await fetch(`${serverUrl}/privacy/data-export`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("ATAccTok")}`,
      },
    });

    if (res.status === 401 && hasRefreshToken()) {
      await getToken();
      return await requestDataExport();
    }
    if (!res.ok) {
      const data = await readJsonSafe(res).catch(() => null);
      return { status: res.status, ok: false, data };
    }
    const blob = await res.blob();
    return { status: 200, ok: true, blob, filename: extractFilename(res) };
  } catch (err) {
    console.error("requestDataExport error:", err);
    return { status: null, ok: false, data: null, error: err };
  }
};

/**
 * Request account anonymization (soft deletion: preserves reviews, removes personal data)
 */
export const requestAnonymization = async () => {
  try {
    assertServerUrl();
    const res = await fetch(`${serverUrl}/privacy/anonymize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("ATAccTok")}`,
      },
    });

    if (res.status === 401 && hasRefreshToken()) {
      await getToken();
      return await requestAnonymization();
    }
    const data = await readJsonSafe(res).catch(() => null);
    return { status: res.status, ok: res.ok, data };
  } catch (err) {
    console.error("requestAnonymization error:", err);
    return { status: null, ok: false, data: null, error: err };
  }
};

//--------------------------------------------------------REFRESH TOKEN
export const getToken = async () => {
    let refToken = localStorage.getItem("ATRefTok");
    try {
        const res = await fetch(`${serverUrl}/auth/refresh-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                token: refToken
            }),
        });
        if (res.status === 401 && res.statusText === 'Unauthorized') {
            window.location.href = '/';
            localStorage.removeItem("ATAccTok");
            localStorage.removeItem("ATRefTok");
            return
        }
        const data = await res.json();
        localStorage.setItem("ATAccTok", data?.data?.accessToken);
        return data
    } catch (err) {
        console.error(err);
    }
};
